import QUERY_KEYS from 'network/config/queryKeys';
import { getAllCategories, getProductsByVendorsandCategories } from 'network/services/goods';
import React, { useMemo } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

export default function useProductsByVendorsCategoriesHook() {
  const [searchParams] = useSearchParams();
  const LIMIT = 60;

  const category = searchParams.get('category');
  const vendor = searchParams.get('vendor');
  const locationId = searchParams.get('locationId');

  const { data: categoryCatalog } = useQuery({
    queryKey: [QUERY_KEYS.PRODUCT_CATEGORIES, 'vendor-page'],
    queryFn: () => getAllCategories(),
    enabled: !!(category && vendor),
    staleTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  const catalogSubCategories = useMemo(() => {
    if (!category || !categoryCatalog?.length) return [];
    const match = categoryCatalog.find(item => String(item.id) === String(category));
    const subs = match?.subCategories ?? [];
    return subs.map(sub => (typeof sub === 'string' ? sub : sub.name)).filter(Boolean);
  }, [categoryCatalog, category]);

  const {
    isFetching: productsByVendorsisFetching,
    data: productsByVendorsData,
    hasNextPage: productsByVendorsHasNext,
    fetchNextPage: productsByVendorsFetchNext,
    isLoading: queryIsLoading,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: [QUERY_KEYS.PRODUCT_BY_VENDORS_CATEGORIES, { categoryId: category, vendorId: vendor, locationId }],
    enabled: !!(category && vendor),
    queryFn: ({ pageParam = 0 }) =>
      getProductsByVendorsandCategories({
        categoryId: category,
        vendorId: vendor,
        locationId: locationId,
        page: pageParam,
        size: LIMIT
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage && typeof lastPage === 'object') {
        const { currentPage, totalPages } = lastPage;

        if (typeof currentPage === 'number' && typeof totalPages === 'number') {
          if (currentPage < totalPages - 1) {
            return currentPage + 1;
          }
          return undefined;
        }
      }

      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  const { seller, subCategories } = useMemo(() => {
    const firstPage = productsByVendorsData?.pages?.[0];

    if (firstPage && typeof firstPage === 'object') {
      const fromApi = firstPage.subCategories || [];
      return {
        seller: firstPage.seller || null,
        subCategories: catalogSubCategories.length > 0 ? catalogSubCategories : fromApi
      };
    }

    return {
      seller: null,
      subCategories: catalogSubCategories
    };
  }, [productsByVendorsData?.pages, catalogSubCategories]);

  const fetchMoreProductsByVendors = React.useCallback(() => {
    if (productsByVendorsHasNext && !isFetchingNextPage && !productsByVendorsisFetching) {
      productsByVendorsFetchNext();
    }
  }, [productsByVendorsFetchNext, productsByVendorsHasNext, isFetchingNextPage, productsByVendorsisFetching]);

  return {
    productsByVendorsData,
    productsByVendorsisFetching,
    fetchMoreProductsByVendors,
    productsByVendorsHasNext,
    seller,
    subCategories,
    isInitialLoading: queryIsLoading && !productsByVendorsData?.pages?.length,
    isFetchingNextPage
  };
}
